import { createRouter, createWebHistory } from 'vue-router';

import { store } from '@/store';
import AccountView from '@/ui/views/AccountView.vue';
import AuthView from '@/ui/views/AuthView.vue';
import BrandFormView from '@/ui/views/BrandFormView.vue';
import BrandListView from '@/ui/views/BrandListView.vue';
import BrandOverviewView from '@/ui/views/BrandOverviewView.vue';
import CreatorLiteView from '@/ui/views/CreatorLiteView.vue';
import DashboardView from '@/ui/views/DashboardView.vue';
import Enable2faView from '@/ui/views/Enable2faView.vue';
import ErrorLogsView from '@/ui/views/ErrorLogsView.vue';
import ForgottenPasswordView from '@/ui/views/ForgottenPasswordView.vue';
import IntegrationsMetaView from '@/ui/views/IntegrationsMetaView.vue';
import LeadgenFormView from '@/ui/views/LeadgenFormView.vue';
import LeadgenFormsView from '@/ui/views/LeadgenFormsView.vue';
import LoginView from '@/ui/views/LoginView.vue';
import ManagedCompaniesView from '@/ui/views/ManagedCompaniesView.vue';
import PasswordResetView from '@/ui/views/PasswordResetView.vue';
import PostListView from '@/ui/views/PostListView.vue';
import ReportView from '@/ui/views/ReportView.vue';
import RuleEditHistoryView from '@/ui/views/RuleEditHistoryView.vue';
import RuleErrorLogView from '@/ui/views/RuleErrorLogView.vue';
import RuleFormView from '@/ui/views/RuleForm/RuleFormView.vue';
import RuleLeadsView from '@/ui/views/RuleLeadsView.vue';
import RuleListView from '@/ui/views/RuleListView.vue';
import RuleOverviewView from '@/ui/views/RuleOverviewView.vue';
import RuleReportView from '@/ui/views/RuleReportView.vue';
import RuleView from '@/ui/views/RuleView.vue';
import RuleWaitingListView from '@/ui/views/RuleWaitingListView.vue';
import SettingsBillingAndPaymentView from '@/ui/views/SettingsBillingAndPaymentView.vue';
import SettingsOrganizationView from '@/ui/views/SettingsOrganizationView.vue';
import SettingsPaymentHistoryView from '@/ui/views/SettingsPaymentHistoryView.vue';
import SettingsTeamView from '@/ui/views/SettingsTeamView.vue';
import SettingsView from '@/ui/views/SettingsView.vue';
import ShoprenterAuthView from '@/ui/views/ShoprenterAuthView.vue';
import ShoprenterInstallView from '@/ui/views/ShoprenterInstallView.vue';
import ShoprenterSettingsView from '@/ui/views/ShoprenterSettingsView.vue';
import SignupView from '@/ui/views/SignupView.vue';
import SupportLoginView from '@/ui/views/SupportViews/SupportLoginView.vue';
import SupportMasqueradeView from '@/ui/views/SupportViews/SupportMasqueradeView.vue';
import VerifyEmailView from '@/ui/views/VerifyEmailView.vue';
import WaitingListView from '@/ui/views/WaitingListView.vue';

const IS_SHOPRENTER_APP = process.env.VUE_APP_FORK === 'shoprenter';
const IS_SUPPORT_BUILD = process.env.VUE_APP_SUPPORT_BUILD == 1;

function hasErrorLogAccess() {
  const user = store.getters['auth/user'];

  return IS_SUPPORT_BUILD
    || (user?.company?.managed_companies?.length > 0 && [ 'admin', 'owner' ].includes(user?.level));
}

const routes = [];

if (IS_SHOPRENTER_APP) {
  routes.push({
    path: '/shoprenter/install',
    name: 'ShoprenterInstall',
    component: ShoprenterInstallView,
  });
  routes.push({
    path: '/auth/shoprenter',
    name: 'ShoprenterAuth',
    component: ShoprenterAuthView,
  });
}
else {
  routes.push({
    path: '/auth',
    redirect: '/auth/login',
    component: AuthView,
    children: [
      {
        path: 'login',
        name: 'Login',
        component: LoginView,
      },
      {
        path: 'signup/:token?',
        name: 'Signup',
        component: SignupView,
      },
      {
        path: 'forgotten-password',
        name: 'ForgottenPassword',
        component: ForgottenPasswordView,
      },
      {
        path: 'reset-password/:token',
        name: 'PasswordReset',
        component: PasswordResetView,
      },
      {
        path: 'verify-email/:token',
        name: 'VerifyEmail',
        component: VerifyEmailView,
      },
    ],
  });
}

routes.push(
  {
    path: '/enable-2fa',
    component: Enable2faView,
  },
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/brands',
    name: 'Brands',
    component: BrandListView,
  },
  {
    path: '/brands/new',
    name: 'BrandForm',
    component: BrandFormView,
  },
  {
    path: '/brands/:id',
    name: 'BrandOverview',
    component: BrandOverviewView,
  },
  {
    path: '/brands/:id/edit',
    name: 'BrandEdit',
    component: BrandFormView,
  },
  {
    path: '/automation',
    name: 'Rules',
    component: RuleListView,
  },
  {
    path: '/automation/new/:action',
    component: RuleFormView,
    meta: {
      isRuleForm: true,
    },
  },
  {
    path: '/automation/:id/edit',
    alias: '/automation/:id/duplicate',
    name: 'RuleForm',
    component: RuleFormView,
    meta: {
      isRuleForm: true,
    },
  },
  {
    path: '/automation/:id',
    component: RuleView,
    children: [
      {
        path: '',
        name: 'Rule',
        redirect: to => `/automation/${to.params.id}/overview`,
      },
      {
        path: 'overview',
        name: 'RuleOverview',
        component: RuleOverviewView,
      },
      {
        path: 'waiting-list',
        name: 'RuleWaitingListView',
        component: RuleWaitingListView,
      },
      {
        path: 'pending-ads',
        redirect: 'waiting-list',
      },
      {
        path: 'ai-risk-review',
        redirect: 'waiting-list',
      },
      {
        path: 'edit-history',
        name: 'RuleEditHistoryView',
        component: RuleEditHistoryView,
      },
      {
        path: 'report',
        name: 'RuleReport',
        component: RuleReportView,
      },
      {
        path: 'leads',
        component: RuleLeadsView,
        children: [
          {
            path: '',
            name: 'RuleLeads',
            meta: { leadModalList: 'rule-leads' },
          },
          {
            // The lead modal over the list.
            path: ':leadId',
            name: 'RuleLead',
            meta: { leadModalList: 'rule-leads' },
          },
        ],
      },
      {
        path: 'error-logs',
        name: 'RuleErrorLog',
        component: RuleErrorLogView,
        meta: {
          accessHandler() {
            return hasErrorLogAccess();
          },
        },
      },
    ],
  },
  {
    path: '/waiting-list',
    name: 'WaitingList',
    component: WaitingListView,
  },
  {
    path: '/leadgen-forms',
    name: 'LeadgenForms',
    component: LeadgenFormsView,
  },
  {
    path: '/leadgen-forms/:id',
    component: LeadgenFormView,
    children: [
      {
        path: '',
        name: 'LeadgenFormLeads',
        meta: { leadModalList: 'leadgen-form-leads' },
      },
      {
        // The lead modal over the list.
        path: 'leads/:leadId',
        name: 'LeadgenFormLead',
        meta: { leadModalList: 'leadgen-form-leads' },
      },
    ],
  },
  {
    path: '/posts',
    component: PostListView,
    children: [
      {
        path: '/',
        name: 'PostListView',
      },
      {
        path: '/posts/:id',
        redirect: to => `/posts/${to.params.id}/original`,
      },
      {
        path: '/posts/:id/:tab',
        name: 'PostView',
      },
    ],
  },
  {
    path: '/magic-tool',
    redirect: '/creator-lite',
  },
  {
    path: '/magic-tool/:id',
    redirect: '/creator-lite',
  },
  {
    path: '/magic-tool/:id/:tab',
    name: 'MagicToolView',
    redirect: '/creator-lite',
  },
  {
    path: '/creator-lite',
    name: 'CreatorLiteView',
    component: CreatorLiteView,
  },
  {
    path: '/creator-lite/new',
    name: 'CreatorLiteViewCreateNew',
    component: CreatorLiteView,
  },
  {
    path: '/creator-lite/:id',
    name: 'CreatorLiteDetailsView',
    component: CreatorLiteView,
  },
  // Redirects from old magic-creation routes
  {
    path: '/magic-creation',
    redirect: '/creator-lite',
  },
  {
    path: '/magic-creation/new',
    redirect: '/creator-lite/new',
  },
  {
    path: '/magic-creation/:id',
    redirect: to => `/creator-lite/${to.params.id}`,
  },
  {
    path: '/report',
    name: 'Report',
    component: ReportView,
  },
  {
    path: '/report/:tokenPart1/:tokenPart2',
    name: 'SharedReport',
    component: ReportView,
  },
  {
    path: '/integrations',
    name: 'Integrations',
    redirect: '/integrations/meta',
    meta: {
      accessHandler() {
        const user = store.getters['auth/user'];
        return user && [ 'admin', 'owner' ].includes(user.level);
      },
    },
  },
  {
    path: '/integrations/meta',
    name: 'IntegrationsMetaProfiles',
    component: IntegrationsMetaView,
    meta: {
      accessHandler() {
        const user = store.getters['auth/user'];
        return user && [ 'admin', 'owner', 'normal' ].includes(user.level);
      },
    },
  },
  {
    path: '/integrations/meta/profiles',
    redirect: '/integrations/meta',
  },
  {
    path: '/error-logs',
    name: 'ErrorLogs',
    component: ErrorLogsView,
    meta: {
      accessHandler() {
        return hasErrorLogAccess();
      },
    },
  },
  {
    path: '/managed-companies',
    name: 'ManagedCompanies',
    component: ManagedCompaniesView,
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardView,
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard',
  },
);

if (!IS_SHOPRENTER_APP) {
  routes.push({
    path: '/account',
    name: 'Account',
    component: AccountView,
    meta: {
      accessHandler() {
        const user = store.getters['auth/user'];
        const currentCompany = store.getters['auth/company'];

        return user
          && currentCompany
          && user.company.id === currentCompany.id;
      },
    },
  });
}

if (IS_SHOPRENTER_APP) {
  routes.push({
    path: '/settings',
    name: 'Settings',
    component: ShoprenterSettingsView,
    meta: {
      accessHandler() {
        const user = store.getters['auth/user'];
        const currentCompany = store.getters['auth/company'];

        return user
          && currentCompany
          && user.company.id === currentCompany.id
          && [ 'admin', 'owner' ].includes(user.level);
      },
    },
  });
}
else {
  routes.push({
    path: '/settings',
    redirect: '/settings/account',
    component: SettingsView,
    meta: {
      accessHandler() {
        const user = store.getters['auth/user'];
        const currentCompany = store.getters['auth/company'];

        return user
          && currentCompany
          && user.company.id === currentCompany.id
          && [ 'admin', 'owner' ].includes(user.level);
      },
    },
    children: [
      {
        path: 'account',
        name: 'SettingsOrganization',
        component: SettingsOrganizationView,
      },
      {
        path: 'billing-and-payment',
        name: 'SettingsBillingAndPayment',
        component: SettingsBillingAndPaymentView,
        meta: {
          accessHandler() {
            const user = store.getters['auth/user'];
            const currentCompany = store.getters['auth/company'];

            return user
              && currentCompany
              && user.company.id === currentCompany.id
              && user.level === 'owner';
          },
        },
      },
      {
        path: 'payment-history',
        name: 'SettingsPaymentHistory',
        component: SettingsPaymentHistoryView,
        meta: {
          accessHandler() {
            const user = store.getters['auth/user'];
            const currentCompany = store.getters['auth/company'];

            return user
              && currentCompany
              && user.company.id === currentCompany.id
              && user.level === 'owner';
          },
        },
      },
      {
        path: 'team',
        name: 'SettingsTeam',
        component: SettingsTeamView,
      },
    ],
  });
}

if (process.env.VUE_APP_SUPPORT_BUILD == 1) {
  routes.push({
    path: '/support/login',
    name: 'SupportLogin',
    component: SupportLoginView,
  });
  routes.push({
    path: '/support/masquerade',
    name: 'SupportMasquerade',
    component: SupportMasqueradeView,
  });
}

export const router = createRouter({
  history: createWebHistory(),
  base: process.env.BASE_URL,
  scrollBehavior(to, from, savedPosition) {
    // Opening or closing the lead modal keeps the list where it was scrolled to.
    if (to.meta.leadModalList && to.meta.leadModalList === from.meta.leadModalList && to.params.leadId !== from.params.leadId) {
      return false;
    }
    if (to.hash) {
      return { el: to.hash };
    }
    if (savedPosition) {
      return savedPosition;
    }

    return { top: 0 };
  },
  routes,
});

router.beforeEach(async (to, from, next) => {
  const onlyPublicRouteNames = [
    'Login',
    'Signup',
    'ForgottenPassword',
    'PasswordReset',
    'VerifyEmail',
    'SharedReport',
    'SupportLogin',
  ];
  const publicRouteNames = [
    'SharedReport',
    'SupportLogin',
    'ShoprenterInstall',
    'ShoprenterAuth',
  ];

  if (IS_SHOPRENTER_APP) {
    onlyPublicRouteNames.push('ShoprenterInstall');
    onlyPublicRouteNames.push('ShoprenterAuth');
  }

  let hasAuthToken = store.getters['auth/hasToken'];
  const hasSupportToken = store.getters['support/hasToken'];
  const toPublicView = onlyPublicRouteNames.includes(to.name);

  async function logout() {
    if (!hasAuthToken) {
      return;
    }

    try {
      await store.dispatch('auth/logout', { gotoLogin: false });
    }
    catch (ignore) {
      // ignore
    }

    hasAuthToken = false;
  }

  if (to.name === 'Signup' && to.params.token && from.name === null) {
    await logout();
    return next();
  }
  else if (IS_SHOPRENTER_APP && (to.name === 'ShoprenterAuth' || to.name === 'ShoprenterInstall')) {
    await logout();
  }

  if (process.env.VUE_APP_SUPPORT_BUILD == 1) {
    if (to.name === 'SupportMasquerade') {
      return hasSupportToken ? next() : next('/');
    }

    if (to.name === 'Login') {
      if (hasSupportToken) {
        return next('/support/masquerade' + window.location.search);
      }

      return next('/support/login' + window.location.search);
    }
  }

  if (!hasAuthToken && !toPublicView) {
    if (IS_SHOPRENTER_APP) {
      window.location.href = 'https://infinite.ad/';
    }

    return next('/auth/login');
  }
  if (hasAuthToken && toPublicView && !publicRouteNames.includes(to.name)) {
    return next('/');
  }

  if (hasAuthToken && !store.getters['auth/user']) {
    try {
      await store.dispatch('auth/fetch');
    }
    catch {
      return next('/auth/login');
    }
  }

  if (
    to &&
    to.meta &&
    to.meta.accessHandler &&
    !to.meta.accessHandler()
  ) {
    return next(from.path);
  }

  return next();
});
